import AddIcon from '@mui/icons-material/Add';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ActionArgs, LoaderArgs } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useLoaderData, useMatches, useOutletContext } from '@remix-run/react';
import * as React from 'react';
import EmptyState from '~/components/AccountSettings/EmptyState';
import ArtworkDesign from '~/components/DesignLab/ArtworkDesign';
import { NavbarEmptyQueue, ToggleDesignType } from '~/components/DesignLab/common';
import { designLabProductSlugAction } from '~/components/DesignLab/utils.server';
import Logo from '~/components/Logo';
import { MuiRemixLink } from '~/components/shared/links';
import { scApi, type Cart as TCart } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { commitSession, getSession } from '~/services/session.server';
import { isPackProduct } from '~/utils/common';
import { scrollbarStyle } from '~/utils/common-styles';
import type { TDesignLab } from '../design-lab';
import { getColorId } from '~/utils/designLab';

export async function loader({ request }: LoaderArgs) {
  const session = await getSession(request);
  const designLabQueue = session.get('designLabQueue') as TDesignLab[];

  if (designLabQueue?.length > 0) {
    const nextProductinQueue = designLabQueue[0];
    const query = getColorId(nextProductinQueue.items_attributes);

    session.set('designLab', nextProductinQueue);

    if (isPackProduct(nextProductinQueue.product)) {
      throw redirect(`/design-lab/${nextProductinQueue.product.slug}?id=${query}`, {
        headers: { 'Set-Cookie': await commitSession(session) },
      });
    } else {
      throw redirect(`/design-lab/bulk/${nextProductinQueue.product.slug}?id=${query}`, {
        headers: { 'Set-Cookie': await commitSession(session) },
      });
    }
  }

  const { user } = await getUpdatedAuthentication(request);
  const accountId = session.get('accountId');

  const artworks = await scApi.getArtworks(user?.accessToken, accountId, session.id, { limit: '100' });

  return json({ artworks: artworks.results });
}

export async function action({ request }: ActionArgs) {
  return await designLabProductSlugAction(request);
}

export default function DesignLab() {
  const { artworks } = useLoaderData<typeof loader>();
  const { setOpenCart } = useOutletContext<{
    setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
  }>();

  const { cart }: { cart: TCart } = useMatches()[0].data;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [designType, setDesignType] = React.useState<number | null>(isMobile ? null : 0);
  const [open, setOpen] = React.useState(false);
  const [selectedCanvas] = React.useState<string | undefined>(undefined);
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const detailRef = React.useRef<null | HTMLDivElement>(null);
  const [touchStartY, setTouchStartY] = React.useState<null | number>(null);
  const [touchInitialTime, setTouchInitialTime] = React.useState<null | number>(null);
  const [initialHeight, setInitialHeight] = React.useState<null | number>(null);
  const detailHeightPercentage = 85;

  const handleElementTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const [bodyElement, htmlElement] = [document?.querySelector('body'), document?.querySelector('html')];

    if (bodyElement && htmlElement) {
      bodyElement.style.overscrollBehaviorY = 'contain';
      htmlElement.style.overscrollBehaviorY = 'contain';
    }

    setInitialHeight(detailRef.current?.offsetHeight || null);
    setTouchInitialTime(e.timeStamp);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleElementDrag = (positionY: number, timeStamp: number) => {
    if (touchStartY && initialHeight) {
      const diff = positionY - touchStartY;
      const value = initialHeight - diff;

      // get acceleration
      const speed = !touchInitialTime ? 0 : (Math.abs(touchStartY - positionY) / (timeStamp - touchInitialTime)) * 100;

      // close on high accelerations
      if (speed > 85) {
        setOpen(false);
        setDesignType(null);
      }

      if (detailRef.current && open) {
        detailRef.current.style.height = `${value}px`;
      }
    }
  };

  const handleElementTouchEnd = () => {
    setTouchStartY(null);
    if (!detailRef.current) return;

    const [bodyElement, htmlElement] = [document?.querySelector('body'), document?.querySelector('html')];

    if (bodyElement && htmlElement) {
      bodyElement.style.overscrollBehaviorY = 'auto';
      htmlElement.style.overscrollBehaviorY = 'auto';
    }

    // if height < 100, close popup, else, return to original height value
    if (Number(detailRef.current.offsetHeight) < 100) {
      setOpen(false);
      setDesignType(null);
    } else detailRef.current.style.height = `${detailHeightPercentage}%`;
  };

  const handleChangeDesignType = (value: number) => {
    if (value !== null) {
      setDesignType(value);
      setOpen(true);
      return;
    }
    if (isMobile) {
      setOpen(false);
      setDesignType(null);
    }
  };

  const handleAddArtwork = (value: fabric.Object) => {
    return;
  };

  const handleExportImageTextures = () => {};

  return (
    <>
      <Stack
        display={{ xs: 'none', md: 'flex' }}
        gridArea="logo"
        justifyContent="center"
        alignItems="center"
        borderRight="1px solid #EFEFEF"
        borderBottom="1px solid #EFEFEF"
      >
        <Logo variant="dark" isResponsive={false} isSmall />
      </Stack>
      <NavbarEmptyQueue cart={cart} setOpenCart={setOpenCart} />
      <Stack
        display={{ xs: 'none', md: 'flex' }}
        gridArea="sidebar"
        overflow="auto"
        pt={2}
        sx={(theme) => ({
          borderRight: '1px solid #EFEFEF',
          justifyItems: 'flex-start',
          alignItems: 'center',

          [theme.breakpoints.down('md')]: {
            position: 'absolute',
            height: 'fit-content',
            bottom: '160px',
            width: '100%',
          },
        })}
      >
        <Button
          variant="contained"
          disabled
          sx={{
            flexDirection: 'column',
            borderRadius: 1,
            p: 1,
            width: '82px',
          }}
        >
          <LibraryAddOutlinedIcon />
          <Typography variant="caption">Products</Typography>
        </Button>

        <Divider sx={{ width: '82px', my: 2 }} />

        <Typography variant="subtitle1" color="text.secondary" px={0.5} fontSize={12} textAlign="center">
          No products in design queue
        </Typography>
      </Stack>
      <Box gridArea="content" overflow={{ xs: 'hidden', md: 'auto' }} height="100%">
        <Grid container height="100%" sx={{ p: 0, m: 0 }} position="relative">
          <Grid
            item
            md={6}
            lg={7}
            xl={5}
            aria-label="canvas-container-outside"
            display="flex"
            alignItems="flex-start"
            width={{ xs: '100%', md: 'unset' }}
            position="relative"
          >
            <Box
              display="grid"
              sx={{ placeItems: 'center', height: '100%', width: '100%', '& > div': { maxWidth: 312 } }}
            >
              <EmptyState
                title="No Products Found"
                description="Looks like there is nothing in your design queue yet."
                imgName="design-lab"
                button={
                  <Button
                    component={MuiRemixLink}
                    href="/shop"
                    sx={{ color: 'primary.main', fontSize: 15 }}
                    startIcon={<AddIcon />}
                  >
                    ADD PRODUCTS
                  </Button>
                }
              />
            </Box>
          </Grid>
          <Grid
            item
            container
            xs={1}
            justifyContent="center"
            alignItems="start"
            bgcolor="#F0F5F9"
            p={2}
            height="100%"
            display={{ xs: 'none', md: 'initial' }}
          >
            <Grid container justifyContent="center" height="fit-content">
              <ToggleDesignType
                designType={designType}
                onChangeDesignType={handleChangeDesignType}
                onExportImageTextures={handleExportImageTextures}
                deactivate
              />
            </Grid>
          </Grid>
          <Grid item md={5} lg={4} xl={6} display={{ xs: 'none', md: 'initial' }}>
            <Grid container borderRadius={4} bgcolor="#FFF">
              <Grid item xs={12} p={2} sx={{ maxHeight: 'calc(100vh - 144px)', ...scrollbarStyle }}>
                {designType === 0 && (
                  <ArtworkDesign
                    artworks={artworks}
                    selectedArtworkInCanvas={selectedCanvas}
                    onAddArtwork={handleAddArtwork}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          {isMobile && (
            <Button
              variant="contained"
              disabled
              sx={{
                flexDirection: 'column',
                borderRadius: 1,
                p: 1,
                width: '82px',
                position: 'absolute',
                top: 16,
                left: 16,
              }}
            >
              <LibraryAddOutlinedIcon />
              <Typography variant="caption">Products</Typography>
            </Button>
          )}
          <Stack width="100%" mt="auto">
            <Stack
              display={{ xs: 'flex', md: 'none' }}
              width="100%"
              px={2}
              mb={2}
              justifyContent="center"
              alignItems="center"
              height="fit-content"
            >
              <Button type="submit" name="_action" disabled value="gotToSummary" variant="contained" fullWidth>
                SAVE DESIGN
              </Button>
            </Stack>
            <Stack
              ref={anchorRef}
              direction="row"
              width="100%"
              height="fit-content"
              justifyContent="center"
              alignItems="center"
              bgcolor="#F0F5F9"
              display={{ md: 'none' }}
              zIndex={(theme) => theme.zIndex.appBar + 4}
            >
              <ToggleDesignType
                designType={designType}
                onChangeDesignType={handleChangeDesignType}
                onExportImageTextures={handleExportImageTextures}
                deactivate
              />
            </Stack>

            {open && (
              <Fade in={open}>
                <Stack
                  display={{ xs: 'flex', md: 'none' }}
                  onClick={(e) => {
                    if (e.target !== e.currentTarget) return;
                    setOpen(false);
                    setDesignType(null);
                  }}
                  zIndex={(theme) => theme.zIndex.appBar + 2}
                  position="absolute"
                  width="100%"
                  bgcolor="#3A3C3F99"
                  justifyContent="end"
                  alignItems="center"
                  sx={[
                    {
                      inset: 0,
                      // Fallback for browser not supporting dv* units
                      height: `calc(100vh - ${anchorRef?.current?.offsetHeight || 80}px)`,
                    },
                    { height: `calc(100dvh - ${anchorRef?.current?.offsetHeight || 80}px)` },
                  ]}
                >
                  <Slide direction="up" in={open}>
                    <Box
                      onTouchStart={(e) => handleElementTouchStart(e)}
                      onTouchMove={(e) => handleElementDrag(e.touches[0].clientY, e.timeStamp)}
                      onTouchEnd={handleElementTouchEnd}
                      width="40%"
                      maxWidth={200}
                      height={10}
                      bgcolor="#FFF"
                      borderRadius={5}
                      mb={1}
                    />
                  </Slide>

                  <Slide direction="up" in={open}>
                    <Stack
                      width="100%"
                      bgcolor="#FFF"
                      ref={detailRef}
                      borderRadius="16px 16px 0px 0px"
                      height={`${detailHeightPercentage}%`}
                      zIndex={(theme) => theme.zIndex.appBar + 3}
                      p={2}
                      sx={{
                        overflowY: 'overlay',
                      }}
                    >
                      {designType === 0 && (
                        <Stack height="100%" width="100%">
                          <ArtworkDesign artworks={artworks} onAddArtwork={handleAddArtwork} />
                        </Stack>
                      )}
                    </Stack>
                  </Slide>
                </Stack>
              </Fade>
            )}
          </Stack>
        </Grid>
      </Box>
      <Box gridArea="footer" display={{ xs: 'none', md: 'flex' }}>
        <Box height={72} bgcolor="#FFF" borderTop="1px solid #EFEFEF" />
      </Box>
    </>
  );
}
